import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  Truck,
  MapPin,
  Clock,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  User,
  Calendar,
  Zap,
  Filter,
  RefreshCw,
  UserPlus,
  Phone,
  ChevronRight,
  History
} from 'lucide-react';
import { toast } from 'sonner';
import JobTimelineModal from '@/components/JobTimelineModal';

const API = process.env.REACT_APP_BACKEND_URL;

export default function DispatchBoard() {
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', priority: '', region: '' });
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [assigning, setAssigning] = useState(false);
  const [showTimelineModal, setShowTimelineModal] = useState(false);
  const [timelineJobId, setTimelineJobId] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const loadBoard = useCallback(async () => {
    try {
      let url = `${API}/api/phs/core/dispatch/board`;
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.priority) params.append('priority', filters.priority);
      if (filters.region) params.append('region', filters.region);
      if (params.toString()) url += `?${params}`;
      
      const res = await axios.get(url, { headers });
      setBoard(res.data);
    } catch (error) {
      console.error('Error loading dispatch board:', error);
      toast.error('Failed to load dispatch board');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadBoard();
    const interval = setInterval(loadBoard, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, [loadBoard]);

  const handleAssignBest = async (jobId) => {
    setAssigning(true);
    try {
      const res = await axios.post(`${API}/api/phs/core/dispatch/assign-best/${jobId}`, {}, { headers });
      toast.success(`Assigned to ${res.data.contractor_name}`);
      loadBoard();
      setShowAssignDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign contractor');
    } finally {
      setAssigning(false);
    }
  };

  const getSLABadge = (status) => {
    const configs = {
      ok: { color: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'On Track' },
      warning: { color: 'bg-amber-100 text-amber-700', icon: Clock, label: 'Warning' },
      breached: { color: 'bg-orange-100 text-orange-700', icon: AlertTriangle, label: 'Breached' },
      critical: { color: 'bg-red-100 text-red-700', icon: AlertCircle, label: 'Critical' },
    };
    const config = configs[status] || configs.ok;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const getPriorityBadge = (priority) => {
    const configs = {
      emergency: { color: 'bg-red-600 text-white', label: 'Emergency' },
      urgent: { color: 'bg-orange-500 text-white', label: 'Urgent' },
      standard: { color: 'bg-blue-100 text-blue-700', label: 'Standard' },
      planned: { color: 'bg-slate-100 text-slate-600', label: 'Planned' },
    };
    const config = configs[priority?.toLowerCase()] || configs.standard;
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const JobCard = ({ job, showAssign = false }) => (
    <Card 
      className="hover:shadow-md transition-shadow cursor-pointer border-l-4"
      style={{
        borderLeftColor: job.sla_status === 'critical' ? '#DC2626' :
                         job.sla_status === 'breached' ? '#F97316' :
                         job.sla_status === 'warning' ? '#F59E0B' : '#E2E8F0'
      }}
      onClick={() => {
        setSelectedJob(job);
        if (showAssign) setShowAssignDialog(true);
      }}
      data-testid={`dispatch-job-${job.job_reference || job.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div>
            <div className="font-mono text-sm font-semibold text-[#0F766E]">
              {job.job_reference || job.id}
            </div>
            <div className="font-medium text-gray-900 line-clamp-1">{job.title}</div>
          </div>
          <div className="flex flex-col items-end gap-1">
            {getPriorityBadge(job.priority)}
            {getSLABadge(job.sla_status)}
          </div>
        </div>
        
        <div className="space-y-1 text-sm text-gray-600">
          <div className="flex items-center gap-1">
            <MapPin className="h-3 w-3" />
            <span className="truncate">{job.site_name || job.park_name || 'Unknown Site'}</span>
          </div>
          <div className="flex items-center gap-1">
            <Truck className="h-3 w-3" />
            <span>{job.trade || job.category || 'General'}</span>
          </div>
          {job.contractor_name && (
            <div className="flex items-center gap-1 text-[#0F766E]">
              <User className="h-3 w-3" />
              <span>{job.contractor_name}</span>
            </div>
          )}
          {job.scheduled_date && (
            <div className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              <span>{new Date(job.scheduled_date).toLocaleDateString()}</span>
            </div>
          )}
        </div>

        {showAssign && (
          <div className="flex gap-2 mt-3">
            <Button 
              size="sm" 
              className="flex-1 bg-[#0F766E] hover:bg-[#115E59]"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedJob(job);
                setShowAssignDialog(true);
              }}
            >
              <UserPlus className="h-3 w-3 mr-1" />
              Assign
            </Button>
            <Button 
              size="sm" 
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                setTimelineJobId(job.job_reference || job.id);
                setShowTimelineModal(true);
              }}
            >
              <History className="h-3 w-3" />
            </Button>
          </div>
        )}
        {!showAssign && (
          <Button 
            size="sm" 
            variant="ghost"
            className="w-full mt-2"
            onClick={(e) => {
              e.stopPropagation();
              setTimelineJobId(job.job_reference || job.id);
              setShowTimelineModal(true);
            }}
          >
            <History className="h-3 w-3 mr-1" />
            View Timeline
          </Button>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <RefreshCw className="h-8 w-8 animate-spin text-[#0F766E]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 md:p-6 space-y-4" data-testid="dispatch-board-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Truck className="h-6 w-6 text-[#0F766E]" />
              Dispatch Board
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              {board?.total || 0} jobs • {board?.contractors_available || 0} contractors available
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={loadBoard}>
              <RefreshCw className="h-4 w-4 mr-1" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-400" />
              <span className="text-sm font-medium">Filters:</span>
            </div>
            <Select value={filters.priority || "all"} onValueChange={(v) => setFilters({...filters, priority: v === "all" ? "" : v})}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priorities</SelectItem>
                <SelectItem value="emergency">Emergency</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
                <SelectItem value="standard">Standard</SelectItem>
                <SelectItem value="planned">Planned</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.status || "all"} onValueChange={(v) => setFilters({...filters, status: v === "all" ? "" : v})}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="Approved">Approved</SelectItem>
                <SelectItem value="Assigned">Assigned</SelectItem>
                <SelectItem value="Scheduled">Scheduled</SelectItem>
              </SelectContent>
            </Select>
            <Input 
              placeholder="Filter by region..." 
              className="w-40"
              value={filters.region}
              onChange={(e) => setFilters({...filters, region: e.target.value})}
            />
          </div>
        </Card>

        {/* Board Columns */}
        <div className="grid md:grid-cols-3 gap-4">
          {/* Awaiting Assignment */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-gray-700 flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-amber-500" />
                Awaiting Assignment
              </h2>
              <Badge variant="outline">{board?.awaiting_assignment?.length || 0}</Badge>
            </div>
            <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-2">
              {board?.awaiting_assignment?.map((job) => (
                <JobCard key={job.id} job={job} showAssign />
              ))}
              {!board?.awaiting_assignment?.length && (
                <Card className="p-6 text-center text-gray-400">
                  <CheckCircle className="h-8 w-8 mx-auto mb-2" />
                  <p>No jobs awaiting assignment</p>
                </Card>
              )}
            </div>
          </div>

          {/* Assigned */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-gray-700 flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-blue-500" />
                Assigned
              </h2>
              <Badge variant="outline">{board?.assigned?.length || 0}</Badge>
            </div>
            <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-2">
              {board?.assigned?.map((job) => (
                <JobCard key={job.id} job={job} />
              ))}
              {!board?.assigned?.length && (
                <Card className="p-6 text-center text-gray-400">
                  <User className="h-8 w-8 mx-auto mb-2" />
                  <p>No assigned jobs</p>
                </Card>
              )}
            </div>
          </div>

          {/* Scheduled */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-gray-700 flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500" />
                Scheduled
              </h2>
              <Badge variant="outline">{board?.scheduled?.length || 0}</Badge>
            </div>
            <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-2">
              {board?.scheduled?.map((job) => (
                <JobCard key={job.id} job={job} />
              ))}
              {!board?.scheduled?.length && (
                <Card className="p-6 text-center text-gray-400">
                  <Calendar className="h-8 w-8 mx-auto mb-2" />
                  <p>No scheduled jobs</p>
                </Card>
              )}
            </div>
          </div>
        </div>

        {/* Assign Dialog */}
        <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Assign Contractor</DialogTitle>
              <DialogDescription>
                {selectedJob?.job_reference} - {selectedJob?.title}
              </DialogDescription>
            </DialogHeader>
            
            {selectedJob && (
              <div className="py-4 space-y-4">
                <div className="p-3 bg-gray-50 rounded-lg space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Priority:</span>
                    {getPriorityBadge(selectedJob.priority)}
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">SLA Status:</span>
                    {getSLABadge(selectedJob.sla_status)}
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Trade:</span>
                    <span>{selectedJob.trade || selectedJob.category || 'General'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Location:</span>
                    <span>{selectedJob.postcode}</span>
                  </div>
                </div>

                <div className="p-3 bg-[#0F766E]/10 rounded-lg border border-[#0F766E]/20">
                  <div className="flex items-center gap-2 text-[#0F766E] font-medium mb-1">
                    <Zap className="h-4 w-4" />
                    Auto-Assign Best Match
                  </div>
                  <p className="text-sm text-gray-600">
                    System will select the best contractor based on region, availability, tier, and capacity.
                  </p>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAssignDialog(false)}>Cancel</Button>
              <Button 
                onClick={() => handleAssignBest(selectedJob?.id)}
                disabled={assigning}
                className="bg-[#0F766E] hover:bg-[#115E59]"
                data-testid="confirm-auto-assign-btn"
              >
                {assigning ? (
                  <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <UserPlus className="h-4 w-4 mr-1" />
                )}
                Auto-Assign Best
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Job Timeline Modal */}
        <JobTimelineModal 
          open={showTimelineModal}
          onClose={() => setShowTimelineModal(false)}
          jobId={timelineJobId}
        />
      </div>
    </Layout>
  );
}
